import { EXPIRY_SKEW } from './constants';
import type { IRoleRef, ISession } from './types';

/**
 * Grades a stored session.
 *
 * Two states, not three: survey-api issues an opaque token against a
 * server-side `auth_session` row and offers no refresh, so `expired` is not
 * recoverable — the difference from `missing` is only the message the user sees.
 */
export enum ValidationStatus {
  valid,
  expired,
  missing
}

export function checkSession(session: Nullable<ISession>): ValidationStatus {
  if (!session) return ValidationStatus.missing;

  const expiresAt = new Date(session.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return ValidationStatus.missing;

  return expiresAt - (Date.now() + EXPIRY_SKEW) <= 0
    ? ValidationStatus.expired
    : ValidationStatus.valid;
}

/** Initials for the avatar. survey-api knows an address, not a name. */
export function initialsFromEmail(email: Nullable<string>): string {
  const local = email?.split('@')[0] ?? '';
  const parts = local.split(/[.\-_+]/).filter(Boolean);

  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (local.slice(0, 2) || 'U').toUpperCase();
}

/** A Team Portal account's roles by survey-api's names for them, or `null` for none — each caller
 *  says the empty case in its own words. */
export function roleNames(roles: readonly IRoleRef[]): Nullable<string> {
  return roles.length > 0 ? roles.map((role) => role.name).join(', ') : null;
}
